/**
 * <glow-card> — reusable glowing feature card, zero dependencies.
 *
 * Works unchanged in: static HTML, Shopify Liquid sections, React 19+ JSX,
 * or anything else that renders HTML. One file, self-registering.
 *
 * Attributes:
 *   title       — card heading
 *   description — body copy
 *   gradient    — CSS gradient string, drives glow + border
 *   delay       — entrance-animation delay in seconds (default 0)
 *
 * Icon: slot any inline SVG (e.g. a Lucide icon) as a child.
 *
 * Usage:
 *   <script src="glow-card.js"></script>
 *   <glow-card title="Hardware" delay="0.1"
 *     description="My entire desktop setup is built for power."
 *     gradient="linear-gradient(137deg, #FF3D77 0%, #FFB1CE 45%, #FF9D3C 100%)">
 *     <svg ...>...</svg>
 *   </glow-card>
 */
class GlowCard extends HTMLElement {
  static get observedAttributes() {
    return ['title', 'description', 'gradient', 'delay'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  connectedCallback() {
    this.render();
    this.observeEntrance();
  }

  attributeChangedCallback() {
    if (this.shadowRoot.firstChild) this.render();
  }

  render() {
    const title = this.getAttribute('title') ?? '';
    const description = this.getAttribute('description') ?? '';
    const gradient = this.getAttribute('gradient') ?? 'linear-gradient(137deg, #666 0%, #999 100%)';
    const delay = parseFloat(this.getAttribute('delay') ?? '0') || 0;

    this.shadowRoot.innerHTML = `
      <style>
        :host{
          position:relative;
          display:flex;
          flex-direction:column;
          justify-content:flex-start;
          align-items:flex-start;
          width:100%;
          max-width:260px;
          margin-inline:auto;
          /* entrance animation — initial state, released by .in */
          opacity:0;
          transform:translateY(30px);
          transition:opacity .8s ease-out ${delay}s, transform .8s ease-out ${delay}s;
        }
        :host(.in){opacity:1;transform:translateY(0);}
        @media (prefers-reduced-motion: reduce){
          :host{opacity:1;transform:none;transition:none;}
        }
        .glow{
          position:absolute;
          width:100%;
          height:var(--glow-card-height,260px);
          opacity:var(--glow-card-glow-opacity,.6);
          border-radius:var(--glow-card-radius,40px);
          pointer-events:none;
          background:${gradient};
          filter:blur(var(--glow-card-blur,45px));
        }
        .card{
          align-self:stretch;
          height:var(--glow-card-height,260px);
          border-radius:var(--glow-card-radius,40px);
          z-index:10;
          overflow:hidden;
          position:relative;
          border:var(--glow-card-border-width,8px) solid transparent;
          background:linear-gradient(var(--glow-card-surface,#1A1A1C),var(--glow-card-surface,#1A1A1C)) padding-box, ${gradient} border-box;
        }
        .inner{
          width:100%;
          height:100%;
          padding:var(--glow-card-padding,28px);
          box-sizing:border-box;
          display:flex;
          flex-direction:column;
          justify-content:var(--glow-card-content-justify,space-between);
        }
        .icon{
          color:var(--glow-card-icon-color,rgba(255,255,255,.9));
          margin-bottom:var(--glow-card-icon-gap,0);
        }
        .icon ::slotted(svg){display:block;}
        h3{
          color:var(--glow-card-title-color,#fff);
          font-weight:500;
          font-size:var(--glow-card-title-size,20px);
          line-height:1.3;
          margin:0 0 var(--glow-card-title-gap,12px);
          letter-spacing:-0.025em;
        }
        p{
          color:var(--glow-card-desc-color,#9CA3AF);
          font-size:var(--glow-card-desc-size,14px);
          line-height:1.6;
          font-weight:400;
          margin:0;
        }
        p::selection, h3::selection{background:rgba(255,255,255,.2);}
        @media (min-width:768px){
          :host{max-width:300px;}
          .glow,.card{height:var(--glow-card-height-md,var(--glow-card-height,300px));}
        }
      </style>
      <div class="glow" part="glow"></div>
      <div class="card" part="card">
        <div class="inner"${this.children.length === 0 ? ' style="justify-content:flex-end"' : ''}>
          ${this.children.length > 0 ? '<div class="icon"><slot></slot></div>' : ''}
          <div>
            <h3 part="title">${escapeHtml(title)}</h3>
            <p part="description">${escapeHtml(description)}</p>
          </div>
        </div>
      </div>
    `;
  }

  observeEntrance() {
    if (!('IntersectionObserver' in window)) {
      this.classList.add('in');
      return;
    }
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          this.classList.add('in');
          io.disconnect();
        }
      }
    }, { threshold: 0.15 });
    io.observe(this);
  }
}

function escapeHtml(s) {
  return s.replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]));
}

if (!customElements.get('glow-card')) {
  customElements.define('glow-card', GlowCard);
}
